import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FiArrowLeft,
  FiCheckCircle,
  FiXCircle,
  FiMapPin,
  FiNavigation,
  FiPhone,
  FiGlobe,
  FiAlertCircle,
  FiRefreshCw
} from 'react-icons/fi';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BaseUrl from '../../../Url/BaseUrl';
// API Configuration

const BusStandApprovals = ({ onBack }) => {
  const [pendingStands, setPendingStands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvingId, setApprovingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchPendingStands();
  }, []);

  const fetchPendingStands = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BaseUrl.baseurl}BusStands/GetPending`);
      setPendingStands(response.data);
    } catch (error) {
      console.error('Error fetching pending bus stands:', error);
      toast.error('Failed to load pending bus stands');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (stand) => {
    try {
      setApprovingId(stand.busStandCode);
      
      // Prepare the updated object with status set to true
      const updatedStand = {
        ...stand,
        status: true
      };

      const response = await axios.put(
        `${BaseUrl.baseurl}BusStands/${stand.busStandCode}`,
        updatedStand
      );

      if (response.status === 200) {
        toast.success(`Bus stand "${stand.busStandName}" approved successfully!`,{ style: {
    background: '#ffffff',
    color: '#1f2937',
    border: '1px solid #e5e7eb'
  }});
        // Remove from pending list
        setPendingStands(prev => 
          prev.filter(item => item.busStandCode !== stand.busStandCode)
        );
      }
    } catch (error) {
      console.error('Error approving bus stand:', error);
      toast.error('Failed to approve bus stand');
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async (stand) => {
    try {
      setRejectingId(stand.busStandCode);
      
      // For rejection, you might want to call a different API endpoint
      // If there's no specific reject endpoint, you can delete or mark as rejected
      // Here I'll show how to delete (if you have delete endpoint)
      const response = await axios.delete(
        `${BaseUrl.baseurl}BusStands/${stand.busStandCode}`
      );

      if (response.status === 200) {
        toast.error(`Bus stand "${stand.busStandName}" rejected`);
        // Remove from pending list
        setPendingStands(prev => 
          prev.filter(item => item.busStandCode !== stand.busStandCode)
        );
      }
    } catch (error) {
      console.error('Error rejecting bus stand:', error);
      toast.error('Failed to reject bus stand');
    } finally {
      setRejectingId(null);
    }
  };

  const filteredStands = pendingStands.filter(stand =>
    stand.busStandName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    stand.busStandCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
    stand.cityVillageCode.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const BusStandCard = ({ stand }) => (
    <div className="approval-card">
      <div className="approval-card__header">
        <div className="approval-card__icon">
          <FiMapPin />
        </div>
        <div className="approval-card__title-section">
          <h3 className="approval-card__name">{stand.busStandName}</h3>
          <span className="approval-card__code">Code: {stand.busStandCode}</span>
        </div>
        <div className="approval-card__badge pending">
          Pending Approval
        </div>
      </div>

      <div className="approval-card__content">
        <div className="approval-card__info-grid">
          <div className="info-item">
            <FiNavigation className="info-icon" />
            <div>
              <span className="info-label">Location</span>
              <span className="info-value">
                {stand.latitude.toFixed(6)}, {stand.longitude.toFixed(6)}
              </span>
            </div>
          </div>

          <div className="info-item">
            <FiGlobe className="info-icon" />
            <div>
              <span className="info-label">City/Village Code</span>
              <span className="info-value">{stand.cityVillageCode}</span>
            </div>
          </div>

          {stand.elevation && (
            <div className="info-item">
              <FiNavigation className="info-icon" />
              <div>
                <span className="info-label">Elevation</span>
                <span className="info-value">{stand.elevation}m</span>
              </div>
            </div>
          )}

          {stand.busStandContactNumber && (
            <div className="info-item">
              <FiPhone className="info-icon" />
              <div>
                <span className="info-label">Contact</span>
                <span className="info-value">{stand.busStandContactNumber}</span>
              </div>
            </div>
          )}
        </div>

        <div className="approval-card__actions">
          <button
            className="btn-approve"
            onClick={() => handleApprove(stand)}
            disabled={approvingId === stand.busStandCode}
          >
            {approvingId === stand.busStandCode ? (
              <>
                <div className="spinner"></div>
                Approving...
              </>
            ) : (
              <>
                <FiCheckCircle /> Approve
              </>
            )}
          </button>
          
          <button
            className="btn-reject"
            onClick={() => handleReject(stand)}
            disabled={rejectingId === stand.busStandCode}
          >
            {rejectingId === stand.busStandCode ? (
              <>
                <div className="spinner"></div>
                Rejecting...
              </>
            ) : (
              <>
                <FiXCircle /> Reject
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="approvals-page">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="approvals-page__header">
      
        <div className="approvals-page__title-section">
          <h1>Bus Stand Approvals</h1>
          <p>Review and approve pending bus stand registrations</p>
        </div>
      </div>

      <div className="approvals-page__content">
        {loading ? (
          <div className="loading-state">
            <div className="spinner-large"></div>
            <p>Loading pending bus stands...</p>
          </div>
        ) : (
          <>
            <div className="approvals-controls">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search by name, code, or city..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                <button className="search-btn">
                  <FiNavigation />
                </button>
              </div>
              
              <div className="stats-card">
                <div className="stats-item">
                  <span className="stats-label">Pending</span>
                  <span className="stats-value">{pendingStands.length}</span>
                </div>
                <button 
                  className="btn-refresh"
                  onClick={fetchPendingStands}
                  disabled={loading}
                >
                  <FiRefreshCw />
                </button>
              </div>
            </div>

            {filteredStands.length === 0 ? (
              <div className="empty-state">
                <FiAlertCircle size="3em" />
                <h3>No pending bus stands found</h3>
                <p>All bus stand requests have been processed.</p>
              </div>
            ) : (
              <div className="approvals-grid">
                {filteredStands.map((stand) => (
                  <BusStandCard key={stand.busStandCode} stand={stand} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BusStandApprovals;